import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { UserCircle, UserPlus, Trash2, Plus, QrCode, Link, Copy, Check, Download, Printer, Search, Filter, Grid, Tag } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import QRCode from 'qrcode';

interface User {
  id: string;
  email: string;
  roles: Role[];
}

interface Role {
  role_id: string;
  role_name: string;
  description: string;
}

interface LoginToken {
  id: string;
  token: string;
  user_id: string;
  expires_at: string;
  created_at: string;
  used_at: string | null;
}

const UserRolesPage: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{ id: string; email: string } | null>(null);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
  });
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [showDropdown, setShowDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // QR Code login token states
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeDataUrl, setQRCodeDataUrl] = useState<string | null>(null);
  const [loginToken, setLoginToken] = useState<LoginToken | null>(null);
  const [tokenUser, setTokenUser] = useState<User | null>(null);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [generatingToken, setGeneratingToken] = useState(false);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRoleFilter, setSelectedRoleFilter] = useState<string>('All');

  // New state for showing role assignment modal
  const [showAssignRoleModal, setShowAssignRoleModal] = useState(false);
  const [userForRoleAssignment, setUserForRoleAssignment] = useState<User | null>(null);

  // New state for multi-select QR code generation
  const [selectedReferees, setSelectedReferees] = useState<string[]>([]);
  const [showBulkQRModal, setShowBulkQRModal] = useState(false);
  const [bulkQRFormat, setBulkQRFormat] = useState<'grid' | 'label'>('grid');
  const [generatingBulkQR, setGeneratingBulkQR] = useState(false);
  const [refereeUsers, setRefereeUsers] = useState<User[]>([]);

  const { user } = useAuthStore();

  useEffect(() => {
    checkAdminAccess();
  }, []);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter users when search term or role filter changes
  useEffect(() => {
    if (users.length > 0) {
      filterUsers();
    }
  }, [users, searchTerm, selectedRoleFilter]);

  // Update referee users list when users change
  useEffect(() => {
    const referees = users.filter(user => 
      user.roles.some(role => role.role_name === 'referee')
    );
    setRefereeUsers(referees);
  }, [users]);

  const filterUsers = () => {
    let filtered = [...users];
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(user => 
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply role filter
    if (selectedRoleFilter !== 'All') {
      filtered = filtered.filter(user => 
        user.roles.some(role => role.role_name === selectedRoleFilter)
      );
    }
    
    setFilteredUsers(filtered);
  };

  const checkAdminAccess = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('You must be logged in to access this page');
        setLoading(false);
        return;
      }

      // Check if user has admin role
      const { data: userRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select(`
          roles!inner (
            role_name
          )
        `)
        .eq('user_id', user.id);

      if (rolesError) throw rolesError;

      const isUserAdmin = userRoles?.some(ur => ur.roles.role_name === 'admin') ?? false;
      setIsAdmin(isUserAdmin);

      if (isUserAdmin) {
        await fetchData();
      } else {
        setError('Access denied. Only administrators can access system settings.');
        setLoading(false);
      }
    } catch (error: any) {
      console.error('Error checking admin access:', error);
      setError('Failed to verify access permissions');
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch all roles
      const { data: rolesData, error: rolesError } = await supabase
        .from('roles')
        .select('*')
        .order('role_name');
      
      if (rolesError) throw rolesError;

      // Get users through the edge function
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'list'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const { users: authUsers } = await response.json();

      // Fetch user roles separately
      const { data: userRolesData, error: userRolesError } = await supabase
        .from('user_roles')
        .select(`
          user_id, 
          role_id,
          roles!user_roles_role_id_fkey(*)
        `);

      if (userRolesError) throw userRolesError;

      // Combine users with their roles
      const usersWithRoles = (authUsers || []).map(user => ({
        id: user.id,
        email: user.email,
        roles: userRolesData
          ?.filter(ur => ur.user_id === user.id)
          .map(ur => ur.roles) || []
      }));

      setRoles(rolesData || []);
      setUsers(usersWithRoles);
      setFilteredUsers(usersWithRoles);
    } catch (error: any) {
      console.error('Error fetching data:', error);
      setError(error.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isAdmin) {
      setError('Access denied. Only administrators can create new users.');
      return;
    }

    if (!newUser.email || !newUser.password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'create',
          email: newUser.email,
          password: newUser.password
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create user');
      }

      const { user } = await response.json();

      if (!user) {
        throw new Error('User creation failed - no user data returned');
      }

      // Close modal and show role selection
      setShowAddUserModal(false);
      setSelectedUser({ id: user.id, email: user.email });
      setShowRoleModal(true);

      // Reset form
      setNewUser({
        email: '',
        password: '',
      });

    } catch (error: any) {
      console.error('Error creating user:', error);
      setError(error.message || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleAssignRoles = async () => {
    if (!selectedUser || selectedRoles.length === 0) {
      setError('Please select at least one role');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Get role IDs for selected roles
      const selectedRoleIds = roles
        .filter(role => selectedRoles.includes(role.role_name))
        .map(role => ({
          user_id: selectedUser.id,
          role_id: role.role_id
        }));

      // Insert role assignments
      const { error: roleError } = await supabase
        .from('user_roles')
        .insert(selectedRoleIds);

      if (roleError) throw roleError;

      // Reset state and refresh
      setShowRoleModal(false);
      setSelectedUser(null);
      setSelectedRoles([]);
      await fetchData();

    } catch (error: any) {
      console.error('Error assigning roles:', error);
      setError(error.message || 'Failed to assign roles');
    } finally {
      setSaving(false);
    }
  };

  // New function to open role assignment modal
  const openRoleAssignmentModal = (user: User) => {
    setUserForRoleAssignment(user);
    setShowAssignRoleModal(true);
  };

  // New function to handle role assignment from modal
  const handleAssignRoleFromModal = async (roleId: string) => {
    if (!userForRoleAssignment) return;
    
    try {
      setSaving(true);
      setError(null);
      
      const { error } = await supabase
        .from('user_roles')
        .insert({
          user_id: userForRoleAssignment.id,
          role_id: roleId
        });
      
      if (error) throw error;
      
      await fetchData();
      setShowAssignRoleModal(false);
      setUserForRoleAssignment(null);
    } catch (error: any) {
      console.error('Error assigning role:', error);
      setError(error.message || 'Failed to assign role');
    } finally {
      setSaving(false);
    }
  };

  const removeRole = async (userId: string, roleId: string) => {
    if (!isAdmin) {
      setError('Only administrators can modify user roles');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      const { error } = await supabase
        .from('user_roles')
        .delete()
        .match({ user_id: userId, role_id: roleId });
      
      if (error) throw error;
      
      await fetchData();
    } catch (error: any) {
      console.error('Error removing role:', error);
      setError(error.message || 'Failed to remove role');
    } finally {
      setSaving(false);
    }
  };

  const hasRole = (user: User, roleId: string) => {
    return user.roles?.some(r => r.role_id === roleId) ?? false;
  };

  const toggleDropdown = (userId: string) => {
    setShowDropdown(prev => prev === userId ? null : userId);
  };

  // Generate a QR code login token for a referee
  const generateLoginToken = async (userId: string) => {
    try {
      setGeneratingToken(true);
      setError(null);
      
      // Find the user
      const user = users.find(u => u.id === userId);
      if (!user) {
        throw new Error('User not found');
      }
      
      // Check if user has referee role
      const hasRefereeRole = user.roles.some(r => r.role_name === 'referee');
      if (!hasRefereeRole) {
        throw new Error('User must have referee role to generate a login token');
      }
      
      // Generate a random token
      const token = Math.random().toString(36).substring(2, 15) + 
                    Math.random().toString(36).substring(2, 15);
      
      // Set expiration date to 24 hours from now
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + 24);
      
      // Save token to database
      const { data, error } = await supabase
        .from('referee_login_tokens')
        .insert([{
          token,
          user_id: userId,
          expires_at: expiresAt.toISOString()
        }])
        .select()
        .single();
      
      if (error) throw error;
      
      // Generate QR code
      const loginUrl = `${window.location.origin}/login?token=${token}`;
      const qrCode = await QRCode.toDataURL(loginUrl);
      
      setQRCodeDataUrl(qrCode);
      setLoginToken(data);
      setTokenUser(user);
      setShowQRModal(true);
      
    } catch (error: any) {
      console.error('Error generating login token:', error);
      setError(error.message || 'Failed to generate login token');
    } finally {
      setGeneratingToken(false);
    }
  };

  const copyTokenLink = () => {
    if (!loginToken) return;
    
    const loginUrl = `${window.location.origin}/login?token=${loginToken.token}`;
    navigator.clipboard.writeText(loginUrl);
    setTokenCopied(true);
    
    setTimeout(() => {
      setTokenCopied(false);
    }, 2000);
  };

  const downloadQrCodeAsPDF = () => {
    if (!qrCodeDataUrl || !tokenUser) return;
    
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    // Add title
    pdf.setFontSize(18);
    pdf.text('Referee Login QR Code', 105, 20, { align: 'center' });
    
    // Add user info
    pdf.setFontSize(12);
    pdf.text(`Referee: ${tokenUser.email}`, 105, 30, { align: 'center' });
    
    // Add expiration info
    if (loginToken) {
      pdf.text(`Expires: ${new Date(loginToken.expires_at).toLocaleString()}`, 105, 40, { align: 'center' });
    }
    
    // Add QR code
    pdf.addImage(qrCodeDataUrl, 'PNG', 65, 50, 80, 80);
    
    // Add instructions
    pdf.setFontSize(10);
    pdf.text('Scan this QR code with the mobile app to log in as a referee.', 105, 140, { align: 'center' });
    pdf.text('This code is valid for 24 hours from generation.', 105, 145, { align: 'center' });
    
    // Save the PDF
    pdf.save(`referee-login-${tokenUser.email.split('@')[0]}.pdf`);
  };

  const printQrCode = () => {
    if (!qrCodeDataUrl) return;
    
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to print the QR code');
      return;
    }
    
    // Create HTML content for printing
    printWindow.document.write(`
      <html>
        <head>
          <title>Referee Login QR Code</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              text-align: center;
              padding: 20px;
            }
            .container {
              max-width: 400px;
              margin: 0 auto;
              border: 1px solid #ccc;
              padding: 20px;
              border-radius: 8px;
            }
            img {
              max-width: 100%;
              height: auto;
            }
            h1 {
              font-size: 18px;
              margin-bottom: 10px;
            }
            p {
              font-size: 14px;
              color: #666;
            }
            .expiry {
              font-size: 12px;
              color: #999;
              margin-top: 20px;
            }
            @media print {
              .no-print {
                display: none;
              }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>Referee Login QR Code</h1>
            <p>${tokenUser?.email}</p>
            <div>
              <img src="${qrCodeDataUrl}" alt="Login QR Code" />
            </div>
            <p class="expiry">Expires: ${loginToken ? new Date(loginToken.expires_at).toLocaleString() : 'N/A'}</p>
          </div>
          <div class="no-print" style="margin-top: 20px;">
            <button onclick="window.print()">Print</button>
            <button onclick="window.close()">Close</button>
          </div>
          <script>
            // Auto print
            setTimeout(() => window.print(), 500);
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
  };

  const downloadQrCodeAsImage = () => {
    if (!qrCodeDataUrl) return;
    
    // Convert data URL to Blob
    const byteString = atob(qrCodeDataUrl.split(',')[1]);
    const mimeString = qrCodeDataUrl.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    
    const blob = new Blob([ab], { type: mimeString });
    saveAs(blob, `referee-login-qr-${tokenUser?.email.split('@')[0] || 'code'}.png`);
  };

  // Toggle referee selection for bulk QR generation
  const toggleRefereeSelection = (userId: string) => {
    setSelectedReferees(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // Select all referees
  const selectAllReferees = () => {
    const allRefereeIds = refereeUsers.map(user => user.id);
    if (selectedReferees.length === allRefereeIds.length) {
      setSelectedReferees([]);
    } else {
      setSelectedReferees(allRefereeIds);
    }
  };

  // Generate bulk QR codes
  const generateBulkQRCodes = async () => {
    if (selectedReferees.length === 0) {
      setError('Please select at least one referee');
      return;
    }

    try {
      setGeneratingBulkQR(true);
      setError(null);

      // Generate tokens and QR codes for all selected referees
      const selectedUsers = users.filter(user => selectedReferees.includes(user.id));
      const tokenData: { user: User; token: string; qrCode: string; expiresAt: Date }[] = [];

      for (const user of selectedUsers) {
        // Generate a random token
        const token = Math.random().toString(36).substring(2, 15) + 
                      Math.random().toString(36).substring(2, 15);
        
        // Set expiration date to 24 hours from now
        const expiresAt = new Date();
        expiresAt.setHours(expiresAt.getHours() + 24);
        
        // Save token to database
        const { error } = await supabase
          .from('referee_login_tokens')
          .insert([{
            token,
            user_id: user.id,
            expires_at: expiresAt.toISOString()
          }]);
        
        if (error) throw error;
        
        // Generate QR code
        const loginUrl = `${window.location.origin}/login?token=${token}`;
        const qrCode = await QRCode.toDataURL(loginUrl, {
          width: 200,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#ffffff'
          }
        });
        
        tokenData.push({ user, token, qrCode, expiresAt });
      }

      // Generate PDF based on selected format
      if (bulkQRFormat === 'grid') {
        generateGridPDF(tokenData);
      } else {
        generateLabelPDF(tokenData);
      }

      setShowBulkQRModal(false);
    } catch (error: any) {
      console.error('Error generating bulk QR codes:', error);
      setError(error.message || 'Failed to generate QR codes');
    } finally {
      setGeneratingBulkQR(false);
    }
  };

  // Generate 1x4 grid PDF (4 cards per page)
  const generateGridPDF = (tokenData: { user: User; token: string; qrCode: string; expiresAt: Date }[]) => {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 10;
    const cardWidth = pageWidth - (margin * 2);
    const cardHeight = (pageHeight - (margin * 2)) / 4; // 4 cards per page
    
    let yPosition = margin;
    
    tokenData.forEach((data, index) => {
      if (index > 0 && index % 4 === 0) {
        // Add a new page after every 4 cards
        pdf.addPage();
        yPosition = margin;
      }
      
      // Card border
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.5);
      pdf.rect(margin, yPosition, cardWidth, cardHeight);
      
      // Card content
      const contentMargin = 5;
      const contentX = margin + contentMargin;
      const contentWidth = cardWidth - (contentMargin * 2);
      
      // Title
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.setTextColor(0, 0, 0);
      pdf.text('REFEREE LOGIN', contentX, yPosition + 10);
      
      // Email
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(80, 80, 80);
      
      // Truncate email if too long
      let email = data.user.email;
      if (email.length > 30) {
        email = email.substring(0, 27) + '...';
      }
      
      pdf.text(email, contentX, yPosition + 18);
      
      // QR Code
      const qrSize = 40;
      const qrX = contentX + contentWidth - qrSize - contentMargin;
      const qrY = yPosition + 10;
      pdf.addImage(data.qrCode, 'PNG', qrX, qrY, qrSize, qrSize);
      
      // One-time use text
      pdf.setTextColor(220, 53, 69); // Red color
      pdf.setFontSize(8);
      pdf.text('One-time use only', contentX, yPosition + 26);
      
      // Expiration
      pdf.setTextColor(120, 120, 120);
      pdf.setFontSize(8);
      pdf.text(`Expires: ${data.expiresAt.toLocaleString()}`, contentX, yPosition + 32);
      
      // Instructions
      pdf.setTextColor(80, 80, 80);
      pdf.setFontSize(8);
      pdf.text('Scan this QR code to log in as a referee', contentX, yPosition + cardHeight - 8);
      
      // Move to next card position
      yPosition += cardHeight;
    });
    
    // Save the PDF
    pdf.save(`referee-login-qr-codes-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // Generate label PDF (51x26mm)
  const generateLabelPDF = (tokenData: { user: User; token: string; qrCode: string; expiresAt: Date }[]) => {
    // Create PDF with custom dimensions for labels
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: [26, 51] // 51mm x 26mm (width x height)
    });
    
    tokenData.forEach((data, index) => {
      if (index > 0) {
        // Add a new page for each label
        pdf.addPage([26, 51]);
      }
      
      // QR Code (make it as large as possible while leaving space for text)
      const qrSize = 20; // 20mm
      const qrX = 2; // 2mm from left
      const qrY = 3; // 3mm from top
      pdf.addImage(data.qrCode, 'PNG', qrX, qrY, qrSize, qrSize);
      
      // Email (truncated if necessary)
      let email = data.user.email;
      if (email.length > 20) {
        email = email.substring(0, 17) + '...';
      }
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(7);
      pdf.setTextColor(0, 0, 0);
      pdf.text('REFEREE', qrSize + 4, 8);
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(6);
      pdf.setTextColor(80, 80, 80);
      pdf.text(email, qrSize + 4, 12);
      
      // One-time use text
      pdf.setTextColor(220, 53, 69); // Red color
      pdf.setFontSize(5);
      pdf.text('One-time use only', qrSize + 4, 16);
      
      // Expiration date in very small text
      pdf.setTextColor(120, 120, 120);
      pdf.setFontSize(4);
      pdf.text(`Expires: ${data.expiresAt.toLocaleDateString()}`, qrSize + 4, 20);
    });
    
    // Save the PDF
    pdf.save(`referee-login-labels-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-4">
        <div className="flex">
          <div className="ml-3">
            <p className="text-sm text-red-700">
              Access denied. Only administrators can access this page.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">User Roles Management</h1>
        <div className="flex space-x-2">
          {/* Bulk QR Code Generation Buttons */}
          {refereeUsers.length > 0 && (
            <>
              <button
                onClick={() => {
                  setBulkQRFormat('grid');
                  setShowBulkQRModal(true);
                }}
                className="inline-flex items-center px-3 py-2 border border-indigo-300 shadow-sm text-sm leading-4 font-medium rounded-md text-indigo-700 bg-white hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Grid className="h-4 w-4 mr-1" />
                Generate QR Codes (1x4)
              </button>
              <button
                onClick={() => {
                  setBulkQRFormat('label');
                  setShowBulkQRModal(true);
                }}
                className="inline-flex items-center px-3 py-2 border border-purple-300 shadow-sm text-sm leading-4 font-medium rounded-md text-purple-700 bg-white hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                <Tag className="h-4 w-4 mr-1" />
                Generate QR Labels (51x26mm)
              </button>
            </>
          )}
          <button
            onClick={() => setShowAddUserModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Add User
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-4 md:items-center">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search users by email..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div className="flex-shrink-0">
              <div className="relative inline-flex">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <select
                  value={selectedRoleFilter}
                  onChange={(e) => setSelectedRoleFilter(e.target.value)}
                  className="block w-full pl-10 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                >
                  <option value="All">All Roles</option>
                  {roles.map(role => (
                    <option key={role.role_id} value={role.role_name}>
                      {role.role_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {refereeUsers.length > 0 && (
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={selectedReferees.length > 0 && selectedReferees.length === refereeUsers.length}
                    onChange={selectAllReferees}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </th>
              )}
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Roles
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={refereeUsers.length > 0 ? 4 : 3} className="px-6 py-4 text-center text-sm text-gray-500">
                  {searchTerm || selectedRoleFilter !== 'All' ? 
                    'No users match your search criteria' : 
                    'No users found'}
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => {
                const isReferee = user.roles.some(r => r.role_name === 'referee');
                return (
                  <tr key={user.id}>
                    {refereeUsers.length > 0 && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isReferee && (
                          <input
                            type="checkbox"
                            checked={selectedReferees.includes(user.id)}
                            onChange={() => toggleRefereeSelection(user.id)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        )}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <UserCircle className="h-10 w-10 text-gray-400" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.email}</div>
                          <div className="text-sm text-gray-500">{user.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-2">
                        {user.roles.map(role => (
                          <span 
                            key={role.role_id} 
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {role.role_name}
                            <button 
                              onClick={() => removeRole(user.id, role.role_id)}
                              className="ml-1 text-blue-500 hover:text-blue-700"
                            >
                              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openRoleAssignmentModal(user)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          disabled={saving}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Assign Role
                        </button>
                        
                        {/* QR Code Login Token Generator - only for users with referee role */}
                        {user.roles.some(r => r.role_name === 'referee') && (
                          <button
                            onClick={() => generateLoginToken(user.id)}
                            disabled={generatingToken}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                          >
                            <QrCode className="h-4 w-4 mr-1" />
                            {generatingToken ? 'Generating...' : 'Generate Login QR'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleCreateUser}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">Add New User</h3>
                      <div className="mt-4 space-y-4">
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                            Email
                          </label>
                          <input
                            type="email"
                            id="email"
                            value={newUser.email}
                            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            required
                          />
                        </div>
                        <div>
                          <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                            Password
                          </label>
                          <input
                            type="password"
                            id="password"
                            value={newUser.password}
                            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-blue-300"
                  >
                    {saving ? 'Creating...' : 'Create User'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddUserModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Role Assignment Modal */}
      {showRoleModal && selectedUser && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Assign Roles</h3>
                    <p className="mt-2 text-sm text-gray-500">
                      Select roles for user: {selectedUser.email}
                    </p>
                    <div className="mt-4 space-y-2">
                      {roles.map(role => (
                        <label key={role.role_id} className="inline-flex items-center mr-4">
                          <input
                            type="checkbox"
                            checked={selectedRoles.includes(role.role_name)}
                            onChange={(e) => {
                              const updatedRoles = e.target.checked
                                ? [...selectedRoles, role.role_name]
                                : selectedRoles.filter(r => r !== role.role_name);
                              setSelectedRoles(updatedRoles);
                            }}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">{role.role_name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleAssignRoles}
                  disabled={saving || selectedRoles.length === 0}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-blue-300"
                >
                  {saving ? 'Assigning...' : 'Assign Roles'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowRoleModal(false);
                    setSelectedUser(null);
                    setSelectedRoles([]);
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Role Assignment Modal */}
      {showAssignRoleModal && userForRoleAssignment && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Assign Role</h3>
                    <p className="mt-2 text-sm text-gray-500">
                      Select a role to assign to: {userForRoleAssignment.email}
                    </p>
                    <div className="mt-4 grid grid-cols-1 gap-3">
                      {roles
                        .filter(role => !userForRoleAssignment.roles.some(r => r.role_id === role.role_id))
                        .map(role => (
                          <button
                            key={role.role_id}
                            onClick={() => handleAssignRoleFromModal(role.role_id)}
                            className="w-full text-left px-4 py-3 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <div className="font-medium text-gray-900">{role.role_name}</div>
                            {role.description && (
                              <div className="text-sm text-gray-500 mt-1">{role.description}</div>
                            )}
                          </button>
                        ))}
                      
                      {roles.filter(role => !userForRoleAssignment.roles.some(r => r.role_id === role.role_id)).length === 0 && (
                        <div className="text-center py-4 text-gray-500">
                          This user already has all available roles.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => setShowAssignRoleModal(false)}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Login Token Modal */}
      {showQRModal && qrCodeDataUrl && loginToken && tokenUser && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Referee Login QR Code</h3>
                    <p className="mt-2 text-sm text-gray-500">
                      This QR code will allow {tokenUser.email} to log in as a referee. The token is valid for 24 hours.
                    </p>
                    
                    <div className="mt-4 flex flex-col items-center">
                      <div className="bg-white p-4 rounded-lg border border-gray-200">
                        <img src={qrCodeDataUrl} alt="Login QR Code" className="w-64 h-64" />
                      </div>
                      
                      <div className="mt-4 w-full">
                        <p className="text-sm font-medium text-gray-700 mb-1">Token expires at:</p>
                        <p className="text-sm text-gray-500 mb-4">
                          {new Date(loginToken.expires_at).toLocaleString()}
                        </p>
                        
                        <div className="flex items-center">
                          <p className="text-sm font-medium text-gray-700 mr-2">Login link:</p>
                          <button
                            onClick={copyTokenLink}
                            className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            {tokenCopied ? (
                              <>
                                <Check className="h-3 w-3 mr-1 text-green-500" />
                                Copied!
                              </>
                            ) : (
                              <>
                                <Copy className="h-3 w-3 mr-1" />
                                Copy Link
                              </>
                            )}
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {`${window.location.origin}/login?token=${loginToken.token}`}
                        </p>
                      </div>
                      
                      {/* Export options */}
                      <div className="mt-6 flex flex-wrap gap-2 justify-center">
                        <button
                          onClick={downloadQrCodeAsPDF}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download PDF
                        </button>
                        <button
                          onClick={downloadQrCodeAsImage}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download Image
                        </button>
                        <button
                          onClick={printQrCode}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <Printer className="h-4 w-4 mr-2" />
                          Print
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => setShowQRModal(false)}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk QR Code Generation Modal */}
      {showBulkQRModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Generate QR Codes for Referees
                    </h3>
                    <p className="mt-2 text-sm text-gray-500">
                      Select the referees you want to generate QR codes for. 
                      {bulkQRFormat === 'grid' ? 
                        ' The codes will be arranged in a 1x4 grid format (4 per page).' : 
                        ' The codes will be formatted for 51x26mm label printing.'}
                    </p>
                    
                    <div className="mt-4 border border-gray-200 rounded-md overflow-y-auto max-h-60">
                      <div className="p-2 border-b border-gray-200 bg-gray-50 flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedReferees.length === refereeUsers.length && refereeUsers.length > 0}
                          onChange={selectAllReferees}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                        />
                        <span className="text-sm font-medium text-gray-700">
                          Select All Referees
                        </span>
                      </div>
                      <ul className="divide-y divide-gray-200">
                        {refereeUsers.map(user => (
                          <li key={user.id} className="p-3 hover:bg-gray-50">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={selectedReferees.includes(user.id)}
                                onChange={() => toggleRefereeSelection(user.id)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3"
                              />
                              <div>
                                <p className="text-sm font-medium text-gray-900">{user.email}</p>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {user.roles.map(role => (
                                    <span 
                                      key={role.role_id} 
                                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                    >
                                      {role.role_name}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={generateBulkQRCodes}
                  disabled={generatingBulkQR || selectedReferees.length === 0}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-blue-300"
                >
                  {generatingBulkQR ? 'Generating...' : `Generate ${selectedReferees.length} QR Code${selectedReferees.length !== 1 ? 's' : ''}`}
                </button>
                <button
                  type="button"
                  onClick={() => setShowBulkQRModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserRolesPage;